/**
 * The adapter between the wire and `@annwn/ui` for the condition overlay
 * (M6-05).
 *
 * The library is presentation-only: it must never see a `SelfCombatSnapshot`, a
 * wound level, or any protocol type (M6-03). So the mapping lives here, on the
 * client side of the boundary, and it is deliberately a *thin* re-projection of
 * the tested M2-06 renderers — it imports `glyphFor`, `partState`, `readoutFor`,
 * `describeWound`, `badgesFor` rather than growing a second body map that could
 * drift from the one the imperative diagram and its tests already agree on.
 *
 * Everything here is a pure function over wire types, for the same reason the
 * old DOM renderers split their rules out: "no number reaches the screen" and
 * "the colour word matches the label" are rules, and a rule you can only check
 * by rendering is a rule nobody checks.
 */

import type { SelfCombatSnapshot, WoundSnapshot } from '@annwn/protocol';
import type { BodyPart, BodyPartState, StatusTagTone, WoundSummary } from '@annwn/ui';

import { readoutFor } from './wound-figure.js';
import {
  bloodProse,
  describeWound,
  partState,
  severityWord,
  windProse,
  woundKind,
} from './wound-prose.js';

/* -------------------------------------------------------------------------- *
 * The momentary states (was `status-strip.ts`, retired in RC2-07)
 *
 * These are the rules the hand-rolled strip carried, moved here rather than
 * deleted: `status-strip.ts` was two things in one file — a set of pure
 * decisions about what a body is showing, and a DOM renderer that nothing had
 * called since M6-05. The renderer is gone and the decisions live beside the
 * other adapters, so the condition panel and the new HUD strip read one
 * answer to "what is true about this body right now" rather than two.
 *
 * The design's anti-goal is unchanged and is the reason none of this formats a
 * number: no numeric health, no damage numbers, no floating combat text. What
 * a bystander could read off you across a clearing — your wind, your blood,
 * whether your guard is up, whether you are on the ground — as words.
 *
 * Tags are for the *momentary* (staggered, prone, guarding): things that are
 * true for a second and change how the next second goes. Prose is for the
 * *cumulative* (blood, wind): things that creep, and that a player should read
 * as a sentence rather than watch as a gauge.
 * -------------------------------------------------------------------------- */

/** A momentary state worth a tag. Ordered by how much it should worry you. */
export interface MomentaryState {
  readonly id: string;
  readonly label: string;
  readonly of: (combat: SelfCombatSnapshot) => boolean;
}

const MOMENTARY: readonly MomentaryState[] = [
  /**
   * Helpless first, because it is the only one that ends the fight.
   *
   * `down` and `prone` were the same word until M2-07 and are now emphatically
   * not: **prone is knocked over, down is out**. One is a bad moment you spend
   * a stamina cost getting up from; the other is a body other people are
   * deciding what to do with. Labelling them alike would have taught players
   * the most expensive wrong lesson in the game.
   */
  { id: 'down', label: 'helpless', of: (c) => c.down },
  { id: 'prone', label: 'on the ground', of: (c) => c.prone && !c.down },
  { id: 'staggered', label: 'staggered', of: (c) => c.staggered },
  { id: 'winded', label: 'winded', of: (c) => c.band === 'winded' },
  { id: 'labouring', label: 'labouring', of: (c) => c.band === 'labouring' },
  { id: 'guarding', label: 'guard up', of: (c) => c.guarding },
  /**
   * Something taken for the pain is working (M4-03).
   *
   * Last, because it is the only good news in the list — and it is here at all
   * because a player whose dice quietly improved and then quietly stopped
   * improving deserves to know which of those was the drug. It says nothing
   * about the wound, which is exactly right: the wound has not changed.
   */
  { id: 'relieved', label: 'the pain is dulled', of: (c) => c.relieved },
];

/**
 * Which momentary states are showing.
 *
 * Pure and exported for the reason `LogBuffer` is split from `LogPane`: "no
 * number appears anywhere in the UI" is an acceptance criterion, and a
 * criterion that can only be checked by rendering is one nobody checks.
 */
export function badgesFor(combat: SelfCombatSnapshot): readonly MomentaryState[] {
  return MOMENTARY.filter((state) => state.of(combat));
}

/**
 * Two clauses, always in the same order, so the sentence has a shape a player
 * learns to skim: how you are bleeding, then how you are breathing.
 */
export function conditionSentence(combat: SelfCombatSnapshot): string {
  return `You are ${bloodProse(combat.bloodStage)}, ${windProse(combat.band)}.`;
}

/**
 * What a channel in progress reads as.
 *
 * Present tense and continuous, because that is what it is: the player is
 * doing this *right now* and the only question they have is whether it is
 * still going. The `…` carries the "keep still" without a tooltip saying so.
 */
const CHANNEL_PROSE: Readonly<Record<string, string>> = {
  forage: 'Gathering…',
  staunch: 'Pressing the wound…',
  bandage: 'Binding the wound…',
  clean: 'Washing the wound…',
  suture: 'Stitching…',
  cauterize: 'Heating the iron…',
  loot: 'Searching them…',
  drag: 'Hauling…',
  build: 'Building…',
  butcher: 'Butchering…',
  cook: 'Cooking…',
  chop: 'Chopping…',
  salvage: 'Working it free…',
};

/**
 * The line shown while a channel runs, or `null`.
 *
 * A **word**, not a progress bar. The design has spent three milestones
 * refusing gauges (M2-06: no health bars, no numbers), and a channel timer
 * would be the first one back in — so this says that you are doing something
 * and stops. What the player actually needs to know is not "3.2 seconds left",
 * it is "this is still happening, so do not walk", and a present participle
 * says that.
 */
export function channelProse(combat: SelfCombatSnapshot): string | null {
  const channel = combat.channel;
  if (!channel) return null;
  return CHANNEL_PROSE[channel.verb] ?? 'Working…';
}

/**
 * The five-state diagram colouring (`whole`…`ruined`) folded down to the
 * library's three-state severity. One place, so the fold cannot be done two
 * subtly different ways.
 */
function toPartState(level: number): BodyPartState {
  const state = partState(level);
  if (state === 'whole') return 'ok';
  if (state === 'bruised' || state === 'wounded') return 'hurt';
  return 'severe';
}

type SnapshotPart = SelfCombatSnapshot['parts'][number];

/** Storybook's legible six-part figure, projected from the human's detailed body. */
const HUMAN_FIGURE = [
  { id: 'head', label: 'Head', glyph: '◉', zones: ['head', 'eye'] },
  { id: 'arm.left', label: 'L. Arm', glyph: '╱', zones: ['arm', 'hand'], side: 'left' },
  { id: 'chest', label: 'Torso', glyph: '▣', zones: ['neck', 'chest', 'abdomen'] },
  { id: 'arm.right', label: 'R. Arm', glyph: '╲', zones: ['arm', 'hand'], side: 'right' },
  { id: 'leg.left', label: 'L. Leg', glyph: '│', zones: ['leg', 'foot'], side: 'left' },
  { id: 'leg.right', label: 'R. Leg', glyph: '│', zones: ['leg', 'foot'], side: 'right' },
] as const;

function membersOf(
  parts: SelfCombatSnapshot['parts'],
  figure: (typeof HUMAN_FIGURE)[number],
): readonly SnapshotPart[] {
  const side = 'side' in figure ? figure.side : undefined;
  return parts.filter(
    (part) =>
      (figure.zones as readonly string[]).includes(part.zone) &&
      (side === undefined || part.side === side),
  );
}

function figurePart(
  parts: SelfCombatSnapshot['parts'],
  figure: (typeof HUMAN_FIGURE)[number],
): BodyPart | null {
  const members = membersOf(parts, figure);
  if (members.length === 0) return null;

  // The representative is a real part id: selecting the box still aims at a
  // real wound, while the figure uses the group’s worst visible condition.
  const representative = members.reduce(
    (worst, part) => {
      if (part.level > worst.level) return part;
      if (part.level === worst.level && part.bleeding && !worst.bleeding) return part;
      return worst;
    },
    members.find((part) => part.id === figure.id) ?? members[0]!,
  );
  const level = Math.max(...members.map((part) => part.level));

  return {
    id: representative.id,
    label: figure.label,
    glyph: figure.glyph,
    state: toPartState(level),
    bleeding: members.some((part) => part.bleeding),
  };
}

export function toBodyParts(parts: SelfCombatSnapshot['parts']): BodyPart[] {
  const figure = HUMAN_FIGURE.map((part) => figurePart(parts, part)).filter(
    (part): part is BodyPart => part !== null,
  );
  if (figure.length === HUMAN_FIGURE.length) return figure;

  // A non-human plan has no honest six-part equivalent. Keep every part, but
  // use the library’s labelled flow rather than stretching glyphs across a
  // humanoid-shaped grid.
  return parts.map((part) => ({
    id: part.id,
    label: part.name,
    glyph: '•',
    state: toPartState(part.level),
    bleeding: part.bleeding,
  }));
}

/** The library's six-word severity tone, from the sim's wound level. */
const SEVERITY_TONES = ['bruised', 'light', 'deep', 'severe', 'crippling', 'mortal'] as const;

function toneFor(level: number): WoundSummary['tone'] {
  const index = Math.max(0, Math.min(SEVERITY_TONES.length - 1, Math.floor(level)));
  return SEVERITY_TONES[index];
}

/**
 * Open wounds as ledger rows. The note is the same prose sentence the diagram's
 * readout uses — provenance, when, how it is being treated — so the ledger and
 * the readout can never say two different things about one wound.
 */
export function toWoundLedger(
  wounds: readonly WoundSnapshot[],
  parts: SelfCombatSnapshot['parts'],
  tick: number,
): WoundSummary[] {
  const nameOf = (partId: string): string =>
    parts.find((part) => part.id === partId)?.name ?? partId;

  return wounds.map((wound) => {
    const partName = nameOf(wound.part);
    const { detail } = describeWound(wound, partName, tick);
    return {
      id: wound.id,
      part: partName,
      severity: severityWord(wound.level),
      kind: woundKind(wound.type),
      tone: toneFor(wound.level),
      note: detail,
      bleeding: wound.bleeding,
    };
  });
}

/** Momentary states as status tags, tone carried in the word and the dot. */
const BADGE_TONES: Readonly<Record<string, StatusTagTone>> = {
  down: 'bleed',
  prone: 'bleed',
  staggered: 'bleed',
  winded: 'cold',
  labouring: 'cold',
  guarding: 'neutral',
  relieved: 'myth',
};

export interface ConditionBadge {
  readonly id: string;
  readonly label: string;
  readonly tone: StatusTagTone;
}

export function conditionBadges(combat: SelfCombatSnapshot): ConditionBadge[] {
  return badgesFor(combat).map((badge) => ({
    id: badge.id,
    label: badge.label,
    tone: BADGE_TONES[badge.id] ?? 'neutral',
  }));
}

/**
 * The prose line under the badges — the same rule the imperative strip used:
 * a running channel ("Binding the wound…") takes the line while it lasts, since
 * whether you are still knelt over the wound is the more urgent question;
 * otherwise it is how you are bleeding and breathing.
 */
export function conditionProse(combat: SelfCombatSnapshot): string {
  return channelProse(combat) ?? conditionSentence(combat);
}

/**
 * The sentence under the figure when a part is inspected — the tested
 * `readoutFor`, reached through the adapter so the React overlay says exactly
 * what the imperative one did. `null` when nothing is selected.
 */
export function selectionReadout(
  combat: SelfCombatSnapshot,
  tick: number,
  selectedPart: string | null,
): string | null {
  if (!selectedPart) return null;
  const part = combat.parts.find((one) => one.id === selectedPart);
  if (!part) return null;
  return readoutFor(part, combat.wounds, tick, combat.scars);
}
